import { isMikrotik, type AccessResourceLike } from './device-access.js';

/**
 * Security check of one resource: known vulnerabilities in what it runs,
 * signs of intrusion, and the security-relevant part of its logs.
 *
 * Linux hosts are read over SSH (installed packages matched against OSV.dev,
 * auth logs, accounts, keys, processes); MikroTiks over SSH too (RouterOS
 * version matched against NVD, users, services, the classic compromise
 * markers, the router's own log).
 */

export type SecuritySeverity = 'critical' | 'high' | 'medium' | 'low' | 'info' | 'unknown';

export type SecurityOverall = 'ok' | 'warn' | 'critical' | 'error';

export type SecurityCategory = 'intrusion' | 'vulnerability' | 'updates' | 'exposure' | 'config';

export interface SecurityFinding {
  /** Stable per kind of finding, e.g. `uid0-extra` — lets the page and alerts key on it. */
  id: string;
  category: SecurityCategory;
  severity: SecuritySeverity;
  title: string;
  detail?: string;
  /** What the finding is about, in fields a treatment can use (user, pid, path, ip, ...). */
  subject?: Record<string, string>;
  /** Ways to deal with it, each applied on its own by an operator. */
  treatments?: SecurityTreatment[];
  /** Why there is no automatic treatment, when there is none — and what to do instead. */
  manual?: string;
}

/**
 * One remedy for a finding. Built by the server at scan time from what the
 * scan saw, stored with the report, and run exactly as stored — the browser
 * only ever names a treatment, never sends commands.
 */
export interface SecurityTreatment {
  id: string;
  label: string;
  description: string;
  /** `inspect` only reads and shows the output; `fix` changes the device. */
  kind: 'fix' | 'inspect';
  /** Interrupts service (reboot, restarts, dropped sessions) — confirmed separately. */
  disruptive: boolean;
  /** Exactly what will run on the device, shown before it does. */
  commands: string[];
  /**
   * For a change that could cut this server off (a firewall): the device
   * reverts it by itself after a few minutes unless these run over a fresh
   * connection afterwards, which proves the server still gets in.
   */
  confirmCommands?: string[];
}

/** A treatment that was applied, for the resource's history. */
export interface SecurityTreatmentRun {
  id: string;
  resourceId: string;
  findingId: string;
  treatmentId: string;
  label: string;
  kind: 'fix' | 'inspect';
  ok: boolean;
  output: string;
  userEmail: string | null;
  appliedAt: number;
}

export interface SecurityVuln {
  id: string;
  /** CVE numbers this advisory covers, when the source names them. */
  aliases: string[];
  severity: SecuritySeverity;
  summary: string;
  /** Version that fixes it on this platform; null when no fix has shipped yet. */
  fixedVersion: string | null;
  url: string;
}

export interface SecurityVulnerablePackage {
  name: string;
  version: string;
  vulns: SecurityVuln[];
  /** How many there were before the stored list was capped. */
  totalVulns?: number;
  /** Upgrade, inspect or remove this package — applied like a finding's, under the id `pkg:<name>`. */
  treatments?: SecurityTreatment[];
  /** What to do by hand when no treatment covers it. */
  manual?: string;
}

/** The finding id a package's treatments are applied under. */
export function packageFindingId(pkg: Pick<SecurityVulnerablePackage, 'name'>): string {
  return `pkg:${pkg.name}`;
}

/** The worst severity among a package's advisories. */
export function packageWorstSeverity(pkg: Pick<SecurityVulnerablePackage, 'vulns'>): SecuritySeverity {
  let worst: SecuritySeverity = 'unknown';
  for (const v of pkg.vulns) if (severityRank(v.severity) < severityRank(worst)) worst = v.severity;
  return worst;
}

/** A vulnerable package as a finding, so its treatments go through the same panel and history. */
export function packageAsFinding(pkg: SecurityVulnerablePackage): SecurityFinding {
  const n = pkg.totalVulns ?? pkg.vulns.length;
  const fixable = pkg.vulns.filter((v) => v.fixedVersion).length;
  return {
    id: packageFindingId(pkg),
    category: 'vulnerability',
    severity: packageWorstSeverity(pkg),
    title: `${pkg.name} ${pkg.version} — ${n} known vulnerabilit${n === 1 ? 'y' : 'ies'}${fixable ? `, ${fixable} with a fix` : ''}`,
    ...(pkg.treatments ? { treatments: pkg.treatments } : {}),
    ...(pkg.manual ? { manual: pkg.manual } : {}),
  };
}

/**
 * What the treatments were built from — stored with the report so they can be
 * rebuilt from the same facts whenever this server learns new ones, without
 * logging into the device again.
 */
export type SecurityTreatmentContext =
  | {
      platform: 'linux';
      packageManager: 'dpkg' | 'rpm' | 'apk' | null;
      loginUser: string;
      loginUsesPassword: boolean;
      fail2ban: boolean;
      /** The address this server reached the host from, as the host saw it. */
      ownAddress: string | null;
    }
  | {
      platform: 'routeros';
      loginUser: string;
      /** Addresses of this server's SSH session on the router — never blocked, always allowed. */
      ownAddresses: string[];
    };

/** A treatment in progress: its output so far, for a page to show live. */
export interface SecurityLiveTreatment {
  running: boolean;
  findingId: string | null;
  treatmentId: string | null;
  label: string | null;
  startedAt: number | null;
  output: string;
}

export interface SecurityLoginSource {
  source: string;
  count: number;
}

export interface SecurityLoginSuccess {
  user: string;
  source: string;
  method: string;
  /** Epoch ms of the latest such login in the window. */
  ts: number | null;
  count: number;
}

export interface SecurityLogEvent {
  ts: number | null;
  kind: 'login-failed' | 'login-ok' | 'sudo' | 'account' | 'other';
  text: string;
}

export interface SecurityReport {
  resourceId: string;
  scannedAt: number;
  durationMs: number;
  platform: 'linux' | 'routeros';
  overall: SecurityOverall;
  /** Set when the check could not run at all. */
  error: string | null;
  /** e.g. "Debian GNU/Linux 12 (bookworm)" or "RouterOS 7.23.3 (stable)". */
  os: string | null;
  kernel: string | null;
  /** Whether the Linux checks ran as root; without it logs and some files are unreadable. */
  privileged: boolean;

  vulnerabilities: {
    /** Where the matching came from — `OSV.dev (Debian:12)`, `NVD`. */
    source: string | null;
    /** Set when the lookup failed; the rest of the report still stands. */
    error: string | null;
    packagesChecked: number;
    total: number;
    /** Vulnerabilities with a fix already published for this platform. */
    fixable: number;
    bySeverity: Record<SecuritySeverity, number>;
    /** Affected packages, the ones with fixes first, capped. */
    packages: SecurityVulnerablePackage[];
  };

  updates: {
    /** Null when the package manager could not be asked. */
    pending: number | null;
    security: number | null;
    rebootRequired: boolean;
    /** RouterOS: newest release on the router's branch. */
    latestVersion: string | null;
  };

  findings: SecurityFinding[];

  logins: {
    windowHours: number;
    failed: number;
    topSources: SecurityLoginSource[];
    topUsers: SecurityLoginSource[];
    successes: SecurityLoginSuccess[];
  };

  /** Most recent security-relevant log lines, newest first. */
  logs: SecurityLogEvent[];

  /** Set by every scan since treatments became rebuildable; older reports lack it. */
  treatmentContext?: SecurityTreatmentContext;
}

/** One row of the fleet overview. */
export interface SecurityOverviewRow {
  resourceId: string;
  name: string;
  kind: string;
  customerId: string | null;
  platform: 'linux' | 'routeros';
  scannedAt: number | null;
  overall: SecurityOverall | null;
  error: string | null;
  os: string | null;
  critical: number;
  high: number;
  fixable: number;
  failedLogins: number;
  intrusion: number;
}

/**
 * The kinds that are Linux servers, and the config fields each keeps its SSH
 * login in (`plain`: username/password/privateKey, `ssh`: sshUsername/...).
 * An allowlist on purpose: plenty of other kinds (cameras, printers, switches)
 * carry a username and password for their web UI and are not SSH hosts.
 */
export const SECURITY_LINUX_KINDS: Record<string, 'plain' | 'ssh'> = {
  'linux-server': 'plain',
  'voip-server': 'ssh',
  'vicidial-dialer': 'ssh',
  magnusbilling: 'ssh',
};

/** Which security check fits a resource, or null when none does. */
export function securityPlatformOf(resource: AccessResourceLike & { kind: string }): 'linux' | 'routeros' | null {
  if (isMikrotik(resource)) return 'routeros';
  return SECURITY_LINUX_KINDS[resource.kind] ? 'linux' : null;
}

export const SECURITY_SEVERITY_ORDER: SecuritySeverity[] = ['critical', 'high', 'medium', 'low', 'unknown', 'info'];

export function severityRank(severity: SecuritySeverity): number {
  return SECURITY_SEVERITY_ORDER.indexOf(severity);
}
